import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import SectionCard from "@/components/SectionCard";
import { UserPlus, ShieldAlert, Check } from "lucide-react";

const EMPTY_FORM = { username: "", full_name: "", password: "", role: "viewer" };

export default function Usuarios() {
  const { user, getUsers, addUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");

  const isAdmin = user?.role === "admin";
  const users = getUsers();

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError("");
    setSuccess("");
  };

  const handleSave = (e) => {
    e.preventDefault();
    setFormError("");
    setSuccess("");

    if (!form.username.trim() || !form.full_name.trim() || !form.password.trim()) {
      setFormError("Completa todos los campos");
      return;
    }

    const exists = users.some((u) => u.username === form.username.trim());
    if (exists) {
      setFormError("El nombre de usuario ya existe");
      return;
    }

    addUser({
      username: form.username.trim(),
      full_name: form.full_name.trim(),
      password: form.password,
      role: form.role,
    });

    setSuccess(`Usuario "${form.username.trim()}" creado correctamente`);
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <SectionCard testid="usuarios-denied">
          <div className="flex flex-col items-center gap-3 py-10 text-slate-500">
            <ShieldAlert className="w-10 h-10 text-amber-500" />
            <p className="text-sm font-medium">No tienes permisos para acceder a esta sección.</p>
            <p className="text-xs text-slate-400">Contacta a un administrador.</p>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Administra las cuentas de acceso al dashboard
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm((v) => !v);
            setFormError("");
            setSuccess("");
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md shadow-violet-300/40 transition hover:opacity-90"
          style={{ background: "linear-gradient(135deg,#4c1d95,#7c3aed,#3b82f6)" }}
        >
          <UserPlus className="w-4 h-4" />
          Agregar usuario
        </button>
      </div>

      {success && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">
          <Check className="w-4 h-4 shrink-0" />
          {success}
        </div>
      )}

      {/* New user form */}
      {showForm && (
        <SectionCard title="Nuevo usuario" testid="usuarios-form">
          <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="new-username" className="block text-sm font-medium text-slate-700 mb-1">
                Usuario
              </label>
              <input
                id="new-username"
                name="username"
                type="text"
                value={form.username}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                placeholder="nombre.usuario"
              />
            </div>

            <div>
              <label htmlFor="new-fullname" className="block text-sm font-medium text-slate-700 mb-1">
                Nombre completo
              </label>
              <input
                id="new-fullname"
                name="full_name"
                type="text"
                value={form.full_name}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                placeholder="Nombre Apellido"
              />
            </div>

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-1">
                Contraseña
              </label>
              <input
                id="new-password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label htmlFor="new-role" className="block text-sm font-medium text-slate-700 mb-1">
                Rol
              </label>
              <select
                id="new-role"
                name="role"
                value={form.role}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
              >
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-md shadow-violet-300/40 transition hover:opacity-90"
                style={{ background: "linear-gradient(135deg,#4c1d95,#7c3aed,#3b82f6)" }}
              >
                Guardar
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setForm(EMPTY_FORM);
                  setFormError("");
                }}
                className="px-5 py-2 rounded-xl text-sm font-medium text-slate-600 border border-slate-300 hover:bg-slate-50 transition"
              >
                Cancelar
              </button>
            </div>

            {formError && (
              <p className="sm:col-span-2 text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}
          </form>
        </SectionCard>
      )}

      {/* Users table */}
      <SectionCard title="Usuarios registrados" testid="usuarios-table">
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                <th className="pb-3 pr-4">Usuario</th>
                <th className="pb-3 pr-4">Nombre</th>
                <th className="pb-3 pr-4">Rol</th>
                <th className="pb-3">Contraseña</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.username} className="text-slate-700">
                  <td className="py-3 pr-4 font-medium tabular-nums">{u.username}</td>
                  <td className="py-3 pr-4">{u.full_name}</td>
                  <td className="py-3 pr-4">
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        u.role === "admin"
                          ? "bg-violet-100 text-violet-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 text-slate-400">••••••</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
